import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
export function HeroSection() {
  const [mousePosition, setMousePosition] = useState({
    x: 0,
    y: 0
  });
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: e.clientX,
        y: e.clientY
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);
  const industries = [{
    name: 'Hospitality',
    route: '/industries/hospitality'
  }, {
    name: 'Conveyancing',
    route: '/industries/conveyancing'
  }, {
    name: 'Renewables',
    route: '/industries/renewables'
  }, {
    name: 'Maritime',
    route: '/industries/maritime'
  }, {
    name: 'Field Service',
    route: '/industries/field-services'
  }];
  // Generate random particles for the intelligence field
  const particles = Array.from({
    length: 30
  }).map((_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    duration: 10 + Math.random() * 20,
    delay: Math.random() * 5
  }));
  return <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden bg-brand-black pt-20 pb-10" data-id="element-295">
      {/* Interactive Background Gradient */}
      <div className="pointer-events-none absolute inset-0 z-0 opacity-20 transition-opacity duration-300" style={{
      background: `radial-gradient(800px circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(219, 127, 74, 0.15), transparent 40%)`
    }} data-id="element-296" />

      {/* Animated Intelligence Field (Particles) */}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none opacity-15" data-id="element-297">
        {particles.map(p => <motion.div key={p.id} className="absolute w-1 h-1 bg-white rounded-full" style={{
        left: `${p.x}%`,
        top: `${p.y}%`
      }} animate={{
        y: [0, -100, 0],
        x: [0, Math.random() * 50 - 25, 0],
        opacity: [0, 0.5, 0]
      }} transition={{
        duration: p.duration,
        repeat: Infinity,
        delay: p.delay,
        ease: 'linear'
      }} data-id="element-298" />)}
      </div>

      {/* Animated Neural Mesh / Grid Background */}
      <div className="absolute inset-0 z-0 opacity-10" data-id="element-299">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" data-id="element-300">
          <defs data-id="element-301">
            <pattern id="grid-hero" width="60" height="60" patternUnits="userSpaceOnUse" data-id="element-302">
              <path d="M 60 0 L 0 0 0 60" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="0.5" data-id="element-303" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid-hero)" data-id="element-304" />
        </svg>
        <div className="absolute inset-0 bg-gradient-to-b from-brand-black via-transparent to-brand-black" data-id="element-305" />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-6 flex flex-col items-center text-center mt-10" data-id="element-306">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        ease: [0.25, 0.1, 0.25, 1]
      }} className="mb-6" data-id="element-307">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs font-medium text-gray-400 uppercase tracking-widest" data-id="element-308">
            <span className="w-1.5 h-1.5 rounded-full bg-brand-orange animate-pulse" data-id="element-309" />
            Enterprise-Grade AI
          </div>
        </motion.div>

        <motion.h1 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.1,
        ease: [0.16, 1, 0.3, 1]
      }} className="font-display text-5xl md:text-7xl lg:text-8xl text-white leading-[0.95] tracking-tight mb-8 relative text-balance" data-id="element-310">
          {/* Soft spotlight glow behind text */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-white/5 blur-[80px] rounded-full pointer-events-none" data-id="element-311" />
          AI Agents Built for <br className="hidden md:block" data-id="element-312" />
          <span className="relative inline-block text-transparent bg-clip-text bg-gradient-to-r from-gray-300 via-gray-400 to-gray-600" data-id="element-313">
            Real Industries.
            {/* Animated highlight sweep */}
            <motion.span className="absolute inset-0 bg-gradient-to-r from-transparent via-brand-orange/30 to-transparent blur-md mix-blend-overlay" animate={{
            x: ['-100%', '200%']
          }} transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut',
            repeatDelay: 2
          }} data-id="element-314" />
          </span>
        </motion.h1>

        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.2,
        ease: [0.16, 1, 0.3, 1]
      }} className="font-body text-lg md:text-xl text-gray-400 max-w-2xl mb-8 leading-relaxed font-light" data-id="element-315">
          Purpose-built AI agents that understand complex workflows across
          hospitality, conveyancing, renewables, maritime, and field service.
        </motion.p>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.25,
        ease: [0.16, 1, 0.3, 1]
      }} className="mb-12" data-id="element-316">
          <p className="text-sm font-medium text-brand-orange/80 tracking-[0.25em] uppercase" data-id="element-317">
            AI agents for real operational environments
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }} className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto mb-12" data-id="element-318">
          <motion.a whileHover={{
          scale: 1.02
        }} whileTap={{
          scale: 0.98
        }} href="#solutions" className="w-full sm:w-auto px-8 py-4 bg-brand-orange hover:bg-brand-deep-orange text-white font-medium rounded-full transition-colors duration-300 flex items-center justify-center shadow-[0_0_20px_rgba(219,127,74,0.2)] hover:shadow-[0_0_40px_rgba(219,127,74,0.4)]" data-id="element-319">
            Explore Industries
          </motion.a>
          <motion.a whileHover={{
          scale: 1.02
        }} whileTap={{
          scale: 0.98
        }} href="#platform" className="w-full sm:w-auto px-8 py-4 bg-white/5 border border-white/10 hover:border-white/30 hover:bg-white/10 text-white font-medium rounded-full transition-colors duration-300 flex items-center justify-center" data-id="element-320">
            See Aijentik in Action
          </motion.a>
        </motion.div>

        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 1,
        delay: 0.6
      }} className="flex flex-col items-center" data-id="element-321">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-[0.25em] mb-4" data-id="element-322">
            Trusted to modernise complex operations
          </p>
          <div className="flex flex-wrap justify-center gap-3" data-id="element-323">
            {industries.map((industry, i) => <Link key={i} to={industry.route} className="text-xs font-medium text-gray-400 bg-[#111] border border-white/5 px-3 py-1 rounded-full hover:text-white hover:border-brand-orange/30 hover:bg-white/5 transition-colors duration-300" data-id="element-324">
                {industry.name}
              </Link>)}
          </div>
        </motion.div>
      </div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 1,
      delay: 1
    }} className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10" data-id="element-325">
        <motion.div animate={{
        y: [0, 10, 0]
      }} transition={{
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-326">
          <ChevronDown className="w-6 h-6 text-white/20" data-id="element-327" />
        </motion.div>
      </motion.div>
    </section>;
}